import moment from "moment";

export interface Plan {
    plan_id: number;
    plan_name: string;
    amount: number;
    type: number;
    category: number;
    currency_code: string;
    discount: number;
    interval: string;
    revenue_limit: number;
    subscription_type: string;
    features: string | null;
    trial_days: number;
    plan_status: number;
    createdAt: string; // ISO date string
    updatedAt: string; // ISO date string
}

export interface CurrentPlan {
    id: number;
    user_id: number;
    shop_id: string;
    created_at: string; // ISO date string
    subscription_expired_date: string; // ISO date string
    plan_id: number;
    status: number;
    plan_name: string;
    amount: number;
    currency_code: string;
    interval: string;
    revenue: number;
    subscription_type: "FREE" | "PAID";
    updated_at: string; // ISO date string
    type: number;
    category: number;
    subscription_id: string | null;
    terminate: number;
    terminated_at: string | null;
    trial_days: number;
    trial_status: number;
    total_orders: number;
}

export const PLANS = {
    FREE_OLD: 1,
    PREMIUM_MONTHLY_OLD: 2,
    PREMIUM_YEARLY_OLD: 3,
    FREE_NEW: 4,
    STARTER: 5,
    UNLIMITED: 6,
    NEW_PLAN: 9,
    CUSTOM_PLAN: 29,
    BASIC_ANNUAL: 10,
    STARTER_ANNUAL: 11,
    UNLIMITED_ANNUAL: 12,
};

// Plans that unlock A/B testing (Volume bundle editor toggle + the /ab-tests page).
export const AB_TESTING_PLANS: number[] = [PLANS.UNLIMITED, PLANS.UNLIMITED_ANNUAL, PLANS.CUSTOM_PLAN];

export const isAbTestingEnabledPlan = (planId: number | undefined): boolean =>
    !!planId && AB_TESTING_PLANS.includes(planId);

// Plans that include Slide Cart. Shops on any other plan need the slide_cart_legacy (grandfathered) flag.
export const SLIDE_CART_PLANS: number[] = [
    PLANS.PREMIUM_MONTHLY_OLD,
    PLANS.PREMIUM_YEARLY_OLD,
    PLANS.STARTER,
    PLANS.STARTER_ANNUAL,
    PLANS.UNLIMITED,
    PLANS.UNLIMITED_ANNUAL,
    PLANS.CUSTOM_PLAN,
];

export const isSlideCartEnabledPlan = (planId: number | undefined): boolean =>
    !!planId && SLIDE_CART_PLANS.includes(planId);

export const PLAN_CATEGORIES = {
    FREE: 1,
    PREMIUM: 2,
};

export const PLAN_STATUS = {
    TERMINATED: 1,
};

export const PLAN_TYPES = {
    MONTHLY: 1,
    YEARLY: 2,
};

export function getPlanValue(amount: number | undefined, type: number, discount?: number): number {
    if (!amount) return 0;

    let finalAmount = amount;

    if (type === PLAN_TYPES.YEARLY) {
        // For yearly, divide the amount by 12 first
        finalAmount = amount / 12;
    }

    if (discount) {
        // Apply percentage discount for both MONTHLY and YEARLY
        finalAmount -= (finalAmount * discount) / 100;
    }

    if (type == 1) {
        return finalAmount < 0 ? 0 : Math.round(finalAmount * 100) / 100;
    }

    return finalAmount < 0 ? 0 : Math.trunc(finalAmount * 100) / 100;
}

export const BILLING_OPTIONS_LABELS = {
    0: "month",
    1: "month",
    2: "year",
};

export const BILLING_OPTIONS = [
    { key: "monthly-1", label: "Monthly", value: 1, discount: 0 },
    { key: "yearly-2", label: "Yearly", value: 2, discount: 25 },
];

export const PLANS_SUBTEXT = {
    1: "Generating revenue below $500/month from bundles",
    2: "Unlimited revenue per month from bundles",
    3: "Unlimited revenue per month from bundles",
    4: "for stores upto 50 orders per month",
    5: "for stores with 51-500 orders per month",
    6: "for stores above 500 orders per month",
    9: "for stores upto 50 orders per month",
    10: "for stores upto 50 orders per month",
    11: "for stores with 51-500 orders per month",
    12: "for stores above 500 orders per month",
    29: "For large stores",
};

export const CHECKS = {
    4: [
        { text: "Standard Bundles", tag: "" },
        { text: "Quantity Breaks", tag: "" },
        { text: "Mix and Match", tag: "" },
        { text: "BXGY Bundles", tag: "" },
        { text: "Frequently Bought Together", tag: "" },
        { text: "Product Add-ons", tag: "" },
        { text: "Buy N, Discount on Cheapest/Costliest", tag: "" },
    ],
    5: [
        { text: "Standard Bundles", tag: "" },
        { text: "Quantity Breaks", tag: "" },
        { text: "Mix and Match", tag: "" },
        { text: "BXGY Bundles", tag: "" },
        { text: "Frequently Bought Together", tag: "" },
        { text: "Product Add-ons", tag: "" },
        { text: "Buy N, Discount on Cheapest/Costliest", tag: "" },
    ],
    6: [
        { text: "Standard Bundles", tag: "" },
        { text: "Quantity Breaks", tag: "" },
        { text: "Mix and Match", tag: "" },
        { text: "BXGY Bundles", tag: "" },
        { text: "Frequently Bought Together", tag: "" },
        { text: "Product Add-ons", tag: "" },
        { text: "Buy N, Discount on Cheapest/Costliest", tag: "" },
    ],
    9: [
        { text: "Standard Bundles", tag: "" },
        { text: "Quantity Breaks", tag: "" },
        { text: "Mix and Match", tag: "" },
        { text: "BXGY Bundles", tag: "" },
        { text: "Frequently Bought Together", tag: "" },
        { text: "Product Add-ons", tag: "" },
        { text: "Buy N, Discount on Cheapest/Costliest", tag: "" },
    ],
    10: [
        { text: "Standard Bundles", tag: "" },
        { text: "Quantity Breaks", tag: "" },
        { text: "Mix and Match", tag: "" },
        { text: "BXGY Bundles", tag: "" },
        { text: "Frequently Bought Together", tag: "" },
        { text: "Product Add-ons", tag: "" },
        { text: "Buy N, Discount on Cheapest/Costliest", tag: "" },
    ],
    11: [
        { text: "Standard Bundles", tag: "" },
        { text: "Quantity Breaks", tag: "" },
        { text: "Mix and Match", tag: "" },
        { text: "BXGY Bundles", tag: "" },
        { text: "Frequently Bought Together", tag: "" },
        { text: "Product Add-ons", tag: "" },
        { text: "Buy N, Discount on Cheapest/Costliest", tag: "" },
    ],
    12: [
        { text: "Standard Bundles", tag: "" },
        { text: "Quantity Breaks", tag: "" },
        { text: "Mix and Match", tag: "" },
        { text: "BXGY Bundles", tag: "" },
        { text: "Frequently Bought Together", tag: "" },
        { text: "Product Add-ons", tag: "" },
        { text: "Buy N, Discount on Cheapest/Costliest", tag: "" },
    ],
};

export const BUNDLE_BUILDER_CHECKS = {
    4: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Product Page Bundle Builder", tag: "" },
        { text: "Mix & Match (Sectional)", tag: "" },
    ],
    5: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Product Page Bundle Builder", tag: "" },
        { text: "Mix & Match (Sectional)", tag: "" },
    ],
    6: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Product Page Bundle Builder", tag: "" },
        { text: "Mix & Match (Sectional)", tag: "" },
    ],
    9: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Product Page Bundle Builder", tag: "" },
        { text: "Mix & Match (Sectional)", tag: "" },
    ],
    10: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Product Page Bundle Builder", tag: "" },
        { text: "Mix & Match (Sectional)", tag: "" },
    ],
    11: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Product Page Bundle Builder", tag: "" },
        { text: "Mix & Match (Sectional)", tag: "" },
    ],
    12: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Product Page Bundle Builder", tag: "" },
        { text: "Mix & Match (Sectional)", tag: "" },
    ],
};

export const CART_CHECKS = {
    4: [
        { text: "Upsells", tag: "" },
        { text: "Spending Milestones", tag: "" },
        { text: "Announcement", tag: "" },
        { text: "Countdown Timer", tag: "" },
    ],
    5: [
        { text: "Upsells", tag: "" },
        { text: "Spending Milestones", tag: "" },
        { text: "Announcement", tag: "" },
        { text: "Countdown Timer", tag: "" },
    ],
    6: [
        { text: "Upsells", tag: "" },
        { text: "Spending Milestones", tag: "" },
        { text: "Announcement", tag: "" },
        { text: "Countdown Timer", tag: "" },
    ],
    9: [
        { text: "Upsells", tag: "" },
        { text: "Spending Milestones", tag: "" },
        { text: "Announcement", tag: "" },
        { text: "Countdown Timer", tag: "" },
    ],
    10: [
        { text: "Upsells", tag: "" },
        { text: "Spending Milestones", tag: "" },
        { text: "Announcement", tag: "" },
        { text: "Countdown Timer", tag: "" },
    ],
    11: [
        { text: "Upsells", tag: "" },
        { text: "Spending Milestones", tag: "" },
        { text: "Announcement", tag: "" },
        { text: "Countdown Timer", tag: "" },
    ],
    12: [
        { text: "Upsells", tag: "" },
        { text: "Spending Milestones", tag: "" },
        { text: "Announcement", tag: "" },
        { text: "Countdown Timer", tag: "" },
    ],
};

export const POST_PURCHASE_CHECKS = {
    4: [{ text: "One Click Upsell", tag: "" }],
    5: [{ text: "One Click Upsell", tag: "" }],
    6: [{ text: "One Click Upsell", tag: "" }],
    9: [{ text: "One Click Upsell", tag: "" }],
    10: [{ text: "One Click Upsell", tag: "" }],
    11: [{ text: "One Click Upsell", tag: "" }],
    12: [{ text: "One Click Upsell", tag: "" }],
};

export const THANK_YOU_CHECKS = {
    4: [{ text: "Upsells", tag: "" }],
    5: [{ text: "Upsells", tag: "" }],
    6: [{ text: "Upsells", tag: "" }],
    9: [{ text: "Upsells", tag: "" }],
    10: [{ text: "Upsells", tag: "" }],
    11: [{ text: "Upsells", tag: "" }],
    12: [{ text: "Upsells", tag: "" }],
};

export const AB_TESTS_CHECKS = {
    6: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Quantity Breaks", tag: "" },
    ],
    12: [
        { text: "Build Your Own Box", tag: "" },
        { text: "Quantity Breaks", tag: "" },
    ],
};

export const CUSTOM_PLAN_CHECKS = {
    29: [
        { text: "1:1 Onboarding", tag: "" },
        { text: "Dedicated Account Manager", tag: "" },
        { text: "Monthly deep upsell & bundle analytics", tag: "" },
        { text: "Monthly bundle optimisation review call", tag: "" },
        { text: "Custom bundle changes", tag: "" },
    ],
};

export const getRemainingTime = (
    timestamp: string | number | Date,
    unit: "day" | "hour" | "minute" = "day",
): string => {
    const targetTimestamp = moment(timestamp);
    const currentTime = moment();
    const timeDifference = targetTimestamp.diff(currentTime);

    if (timeDifference <= 0) {
        return `0 ${unit}s`;
    }

    const days = Math.floor(timeDifference / (24 * 60 * 60 * 1000));
    const hours = Math.floor((timeDifference % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((timeDifference % (60 * 60 * 1000)) / (60 * 1000));

    switch (unit) {
        case "minute":
            return minutes > 0
                ? `${minutes} minute${minutes >= 1 ? "s" : ""}`
                : `${hours} hour${hours >= 1 ? "s" : ""}`;

        case "hour":
            return hours > 0 ? `${hours} hour${hours >= 1 ? "s" : ""}` : `${days} day${days >= 1 ? "s" : ""}`;

        case "day":
        default:
            return `${days || 1} day${days >= 1 ? "s" : ""}`;
    }
};

export function calculateTrialDaysLeft(createdAt: string, trialDays: number) {
    const createdDate = moment(createdAt); // Parse createdAt using Moment.js
    const currentDate = moment(); // Get current date using Moment.js

    // Calculate the trial end date
    const trialEndDate = createdDate.add(trialDays, "days");

    // Calculate the difference in days
    let daysLeft = trialEndDate.diff(currentDate, "days", true); // true for fractional days

    daysLeft = Math.ceil(Math.max(0, daysLeft));
    daysLeft = daysLeft > trialDays ? trialDays : daysLeft;
    return daysLeft;
}
